import type { TimeSlot, CalendarEvent } from '../types'

/**
 * Generate time slots for a day
 */
export function generateTimeSlots(
  startHour: number,
  endHour: number,
  slotDuration: number
): TimeSlot[] {
  const slots: TimeSlot[] = []

  for (let hour = startHour; hour < endHour; hour++) {
    for (let minute = 0; minute < 60; minute += slotDuration) {
      slots.push({
        hour,
        minute,
        label: `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`,
      })
    }
  }

  return slots
}

/**
 * Get start of day
 */
export function startOfDay(date: Date): Date {
  const result = new Date(date)
  result.setHours(0, 0, 0, 0)
  return result
}

/**
 * Get end of day
 */
export function endOfDay(date: Date): Date {
  const result = new Date(date)
  result.setHours(23, 59, 59, 999)
  return result
}

/**
 * Get start of week (Monday)
 */
export function startOfWeek(date: Date): Date {
  const result = new Date(date)
  const day = result.getDay()
  const diff = result.getDate() - day + (day === 0 ? -6 : 1) // Monday
  result.setDate(diff)
  result.setHours(0, 0, 0, 0)
  return result
}

/**
 * Get end of week (Sunday)
 */
export function endOfWeek(date: Date): Date {
  const result = startOfWeek(date)
  result.setDate(result.getDate() + 6)
  result.setHours(23, 59, 59, 999)
  return result
}

/**
 * Get start of month
 */
export function startOfMonth(date: Date): Date {
  const result = new Date(date)
  result.setDate(1)
  result.setHours(0, 0, 0, 0)
  return result
}

/**
 * Get end of month
 */
export function endOfMonth(date: Date): Date {
  const result = new Date(date)
  result.setMonth(result.getMonth() + 1, 0)
  result.setHours(23, 59, 59, 999)
  return result
}

/**
 * Add days to a date
 */
export function addDays(date: Date, days: number): Date {
  const result = new Date(date)
  result.setDate(result.getDate() + days)
  return result
}

/**
 * Add months to a date
 */
export function addMonths(date: Date, months: number): Date {
  const result = new Date(date)
  result.setMonth(result.getMonth() + months)
  return result
}

/**
 * Check if two dates are the same day
 */
export function isSameDay(date1: Date, date2: Date): boolean {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  )
}

/**
 * Check if date is today
 */
export function isToday(date: Date): boolean {
  return isSameDay(date, new Date())
}

/**
 * Check if date is weekend (Saturday or Sunday)
 */
export function isWeekend(date: Date): boolean {
  const day = date.getDay()
  return day === 0 || day === 6 // Sunday or Saturday
}

/**
 * Get days in month
 */
export function getDaysInMonth(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()
}

/**
 * Get week days starting from Monday
 */
export function getWeekDays(weekStart: Date): Date[] {
  const days: Date[] = []
  for (let i = 0; i < 7; i++) {
    days.push(addDays(weekStart, i))
  }
  return days
}

/**
 * Parse event date (handles both Date and string)
 */
export function parseEventDate(date: Date | string): Date {
  if (date instanceof Date) return date
  return new Date(date)
}

/**
 * Get event end time
 */
export function getEventEnd(event: CalendarEvent): Date {
  const start = parseEventDate(event.start)
  return new Date(start.getTime() + event.duration * 60000)
}

/**
 * Filter events for a specific day
 */
export function getEventsForDay(events: CalendarEvent[], date: Date): CalendarEvent[] {
  return events.filter((event) => {
    const eventDate = parseEventDate(event.start)
    return isSameDay(eventDate, date)
  })
}

/**
 * Filter events for a date range
 */
export function getEventsInRange(
  events: CalendarEvent[],
  start: Date,
  end: Date
): CalendarEvent[] {
  return events.filter((event) => {
    const eventStart = parseEventDate(event.start)
    return eventStart >= start && eventStart <= end
  })
}

/**
 * Check if events overlap
 */
export function eventsOverlap(event1: CalendarEvent, event2: CalendarEvent): boolean {
  const start1 = parseEventDate(event1.start).getTime()
  const end1 = getEventEnd(event1).getTime()
  const start2 = parseEventDate(event2.start).getTime()
  const end2 = getEventEnd(event2).getTime()

  return start1 < end2 && end1 > start2
}

/**
 * Sort events by start time
 */
export function sortEventsByStart(events: CalendarEvent[]): CalendarEvent[] {
  return [...events].sort((a, b) => {
    const aStart = parseEventDate(a.start).getTime()
    const bStart = parseEventDate(b.start).getTime()
    return aStart - bStart
  })
}
